import React, { useEffect, useRef, useState } from "react";

const fmt = (n: number) => new Intl.NumberFormat("en-US").format(n);
const parseKpi = (value: unknown, fallback: number) => {
  const parsed = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const Reveal: React.FC<{
  children: React.ReactNode;
  className?: string;
  variant?: "fadeUp" | "fadeLeft" | "fadeRight" | "scale";
  delayMs?: number;
  opts?: IntersectionObserverInit;
}> = ({ children, className = "", variant = "fadeUp", delayMs = 0, opts }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.22,
        rootMargin: "0px 0px -18% 0px",
        ...(opts || {}),
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [opts]);

  const base = "transition-all duration-700 ease-out will-change-transform will-change-opacity";
  const style = delayMs ? ({ transitionDelay: `${delayMs}ms` } as React.CSSProperties) : undefined;
  const hiddenMap: Record<string, string> = {
    fadeUp: "opacity-0 translate-y-6",
    fadeLeft: "opacity-0 -translate-x-6",
    fadeRight: "opacity-0 translate-x-6",
    scale: "opacity-0 scale-[0.98]",
  };

  return (
    <div
      ref={ref}
      style={style}
      className={`${className} ${base} ${shown ? "opacity-100 translate-y-0 translate-x-0 scale-100" : hiddenMap[variant]}`}
    >
      {children}
    </div>
  );
};

function useCountUpOnce(target: number, start: boolean, durationMs = 1100) {
  const [value, setValue] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!start || done) return;

    let raf = 0;
    const t0 = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / durationMs);
      setValue(Math.round(target * easeOutCubic(p)));
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setValue(target);
        setDone(true);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [done, durationMs, start, target]);

  return done ? target : value;
}

const AboutGabbroAlt: React.FC<{ content?: any }> = ({ content }) => {
  const [kpiVisible, setKpiVisible] = useState(false);
  const kpiRef = useRef<HTMLDivElement | null>(null);
  const tphTarget = parseKpi(content?.kpi_tph, 800);
  const dailyTarget = parseKpi(content?.kpi_daily, 8928);
  const monthlyTarget = parseKpi(content?.kpi_monthly, 240000);
  const tph = useCountUpOnce(tphTarget, kpiVisible, 950);
  const daily = useCountUpOnce(dailyTarget, kpiVisible, 1050);
  const monthly = useCountUpOnce(monthlyTarget, kpiVisible, 1250);

  useEffect(() => {
    const el = kpiRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setKpiVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25, rootMargin: "0px 0px -20% 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-4 py-10 space-y-14">
        {/* ================= HERO ABOUT ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* LEFT IMAGE */}
          <Reveal variant="fadeLeft" className="lg:col-span-7">
            <div className="relative overflow-hidden rounded-3xl border bg-white shadow-sm">
              <img
                src={content?.about_main_image || "/gabbro/quarry-main.jpg"}
                alt="Quarry Operations"
                className="w-full h-[320px] md:h-[460px] object-cover"
              />
              <div className="absolute left-5 top-5 rounded-2xl border bg-white/90 backdrop-blur px-4 py-3">
                <div className="text-[11px] uppercase tracking-widest text-gray-500">About</div>
                <div className="text-sm font-semibold text-gray-900">
                  {content?.about_main_box_title || "Dar Bani Omar Trading & Contracting LLC"}
                </div>
              </div>
              <div className="mt-8 h-1 w-full rounded-full bg-gradient-to-r from-yellow-500 via-yellow-400 to-red-500" />
            </div>
          </Reveal>

          {/* RIGHT ABOUT TEXT */}
          <Reveal variant="fadeRight" className="lg:col-span-5">
            <div className="rounded-3xl border bg-gray-50 p-6 md:p-7">
              <h2 className="text-2xl font-bold text-gray-900">{content?.about_title || "About"}</h2>

              <div className="mt-4 space-y-4 text-sm text-gray-700 leading-relaxed text-justify">
                <div dangerouslySetInnerHTML={{ __html: (content?.about_desc || `Dar Bani Omar Trading & Contracting LLC is a subsidiary of Hamdan Mining International LLC – one of the group companies of Hamdan Holding Group LLC based in the Dhofar region of the Sultanate of Oman owned by Sh. Hamed Bin Hamood Al Ghafri and his family.<br/><br/>The investment stake of Hamdan Holding Group in DBO is 90%. Dar Bani Omar Trading & Contracting LLC is an international business house having strategic local partnerships in Oman and Qatar. It is one of the leading companies in Oman that produces quality crushed aggregates. It serves the varied needs of its customers with an emphasis on supreme quality of service and products.<br/><br/>Being a customer-driven business, this organization believes in uncompromising commitment. Our priority has always been to provide the highest quality of services and product while operating with detailed and in-depth focus on safety, health, and environmental related issues, with no tolerance towards any unsafe activities.`).replace(/\n/g, '<br/>') }} />
              </div>
            </div>
          </Reveal>
        </div>

        {/* ================= CORE FOCUS AREAS + IMAGE ================= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* LEFT ICON CARDS */}
          <Reveal variant="fadeLeft" className="lg:col-span-7 h-full">
            <div className="rounded-3xl border bg-white p-7 shadow-sm h-full">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">
                    What we stand for
                  </div>
                  <h3 className="mt-2 text-xl font-bold text-gray-900">
                    Core Focus Areas
                  </h3>
                </div>
                <div className="hidden md:flex items-center gap-2">
                  <span className="h-2 w-10 rounded-full bg-yellow-500" />
                  <span className="text-xs uppercase tracking-widest text-gray-500">
                    HSE • Quality • Supply
                  </span>
                </div>
              </div>

              {/* ✅ IMPORTANT: items-stretch */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch">
                {[0, 1, 2].map((i) => (
                  <Reveal key={i} variant="fadeUp" delayMs={i * 120} className="h-full">
                    <div className="rounded-2xl border bg-gray-50 p-5 h-full flex flex-col">
                      <div className="h-11 w-11 rounded-2xl bg-white border flex items-center justify-center overflow-hidden">
                        {content?.[`focus_${i + 1}_icon`] ? (
                          <img src={content[`focus_${i + 1}_icon`]} className="h-full w-full object-contain p-2" alt={content?.[`focus_${i + 1}_title`] || "Focus"} />
                        ) : (
                          <svg viewBox="0 0 24 24" className="h-6 w-6 text-red-700" fill="none" stroke="currentColor" strokeWidth="2">
                            {[
                              "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
                              "M12 2l3 5 5 1-3 4 .5 5-5.5-2.5L6.5 17 7 12 4 8l5-1 3-5z",
                              "M3 7h11v10H3z M14 10h4l3 3v4h-7z"
                            ][i] && <path d={[
                              "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
                              "M12 2l3 5 5 1-3 4 .5 5-5.5-2.5L6.5 17 7 12 4 8l5-1 3-5z",
                              "M3 7h11v10H3z M14 10h4l3 3v4h-7z"
                            ][i]} />}
                            {i === 1 && <path d="M9 12l2 2 4-4" />}
                            {i === 2 && (
                              <>
                                <circle cx="7" cy="19" r="2" />
                                <circle cx="18" cy="19" r="2" />
                              </>
                            )}
                          </svg>
                        )}
                      </div>

                      <div className="mt-4 text-sm font-bold text-gray-900">
                        {content?.[`focus_${i + 1}_title`] || ["Safety & HSE", "Quality Products", "Strategic Supply"][i]}
                      </div>

                      <p className="mt-2 text-sm text-gray-700 leading-relaxed flex-1">
                        {content?.[`focus_${i + 1}_desc`] || [
                          "Zero tolerance for unsafe activities with strong safety, health, and environmental practices.",
                          "Reliable, consistent aggregates serving concrete and road construction requirements.",
                          "Strong logistics and production capacity to support project timelines."
                        ][i]}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </Reveal>

          {/* RIGHT IMAGE (already correct) */}
          <Reveal variant="fadeRight" className="lg:col-span-5 h-full">
            <div className="rounded-3xl border overflow-hidden bg-white shadow-sm h-full">
              <img
                src={content?.about_city_image || "/gabbro/qatar-city.jpg"}
                alt="Qatar City"
                className="w-full h-full object-cover min-h-[260px]"
              />
            </div>
          </Reveal>
        </div>


        {/* ================= VISION / MISSION / VALUES ================= */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {/* VISION */}
          <Reveal variant="fadeUp" delayMs={0} className="h-full">
            <div className="rounded-3xl border bg-white p-7 shadow-sm h-full flex flex-col">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl overflow-hidden">
                  <img
                    src={content?.vision_icon || "/gabbro/vission_1.png"}
                    alt="Vission Icon"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="mt-1 text-lg font-bold text-gray-900">VISION</h3>
                </div>
              </div>

              {/* ✅ flex-1 so it balances height */}
              <p className="mt-4 text-sm text-gray-700 leading-relaxed text-justify flex-1">
                {content?.vision_desc || "To be the leading provider of crushed quarry materials for concrete and road construction work in the region & Qatar."}
              </p>
            </div>
          </Reveal>

          {/* MISSION */}
          <Reveal variant="fadeUp" delayMs={120} className="h-full">
            <div className="rounded-3xl border bg-white p-7 shadow-sm h-full flex flex-col">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl overflow-hidden">
                  <img
                    src={content?.mission_icon || "/gabbro/mission_2.png"}
                    alt="Mission Icon"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="mt-1 text-lg font-bold text-gray-900">MISSION</h3>
                </div>
              </div>

              <p className="mt-4 text-sm text-gray-700 leading-relaxed text-justify flex-1">
                {content?.mission_desc || "To establish and develop reliable sources of crushed aggregate and sand to improve significantly the capacity and efficiency of our quarrying and crushing operations; through investment and continuous monitoring and reviewing of our performance to provide a strategic supply of crushed aggregates and sand."}
              </p>
            </div>
          </Reveal>

          {/* VALUES */}
          <Reveal variant="fadeUp" delayMs={240} className="h-full">
            <div className="rounded-3xl border bg-white p-7 shadow-sm h-full flex flex-col">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-2xl overflow-hidden">
                  <img
                    src={content?.values_icon || "/gabbro/value.jpg"}
                    alt="Value Icon"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="mt-1 text-lg font-bold text-gray-900">VALUES</h3>
                </div>
              </div>

              {/* ✅ Make the list area fill space */}
              <div className="mt-4 flex-1">
                <ul className="space-y-3 text-sm text-gray-700 leading-relaxed text-justify">
                  {(content?.values_list ? content.values_list.split('\n') : [
                    "These values underpin all our business activities: To have respect and harmony for the environment, our employees, us suppliers and our customers.",
                    "Conducts ourselves ethically and with integrity at all times to carry out all our activities in a safe manner based on best international.",
                    "Practice to maintain and provide quality products to all our customers with us."
                  ]).map((val: string, i: number) => (
                    <li key={i} className="flex gap-2">
                      <span className="mt-[7px] h-2 w-2 rounded-full bg-red-500 flex-shrink-0" />
                      {val}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>


        {/* ================= PRODUCTION CAPACITY (KPI count-up REPEATABLE) ================= */}
        <Reveal variant="scale">
          <div ref={kpiRef} className="rounded-3xl border bg-white p-6 md:p-7 shadow-sm">
            <div className="rounded-[26px] bg-white">
              {/* Header */}
              <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">Operations</div>
                  <h3 className="mt-4 text-xl font-bold text-gray-900">Production Capacity Summary</h3>
                </div>

                {/* Stat pills */}
                <div className="flex w-full flex-wrap items-center justify-start gap-2 md:w-auto">
                  <span className="inline-flex items-center gap-2 rounded-full border bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-700">
                    <span className="h-2 w-2 rounded-full bg-yellow-500" />
                    Plant-1
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-700">
                    <span className="h-2 w-2 rounded-full bg-yellow-500" />
                    Plant-2
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-700">
                    <span className="h-2 w-2 rounded-full bg-yellow-500" />
                    Combined
                  </span>
                </div>
              </div>

              {/* KPI Cards (COUNT UP) */}
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-2xl border bg-gray-50 p-5">
                  <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">TPH</div>
                  <div className="mt-2 text-2xl font-bold text-red-700 tabular-nums">{fmt(tph)}</div>
                  <div className="mt-1 text-xs text-gray-600">tons per hour (combined)</div>
                </div>

                <div className="rounded-2xl border bg-gray-50 p-5">
                  <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">Daily Production</div>
                  <div className="mt-2 text-2xl font-bold text-red-700 tabular-nums">{fmt(daily)}</div>
                  <div className="mt-1 text-xs text-gray-600">tons (combined)</div>
                </div>

                <div className="rounded-2xl border bg-gray-50 p-5">
                  <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">Monthly Production</div>
                  <div className="mt-2 text-2xl font-bold text-red-700 tabular-nums">{fmt(monthly)}</div>
                  <div className="mt-1 text-xs text-gray-600">tons (combined)</div>
                </div>
              </div>

              {/* Table + Image */}
              <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
                {/* Table */}
                <Reveal variant="fadeLeft" className="lg:col-span-7">
                  <div className="overflow-hidden rounded-2xl border">
                    <div className="flex items-center justify-between bg-gray-900 px-4 py-3">
                      <div className="text-sm font-semibold text-white">Capacity Table</div>
                      <div className="text-xs text-white/70">Plant comparison</div>
                    </div>

                    <div className="overflow-x-auto bg-white">
                      <table className="w-full border-collapse text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="border px-4 py-2 text-left">Particulars</th>
                            <th className="border px-4 py-2 text-center">PLANT-1</th>
                            <th className="border px-4 py-2 text-center">PLANT-2</th>
                            <th className="border px-4 py-2 text-center">Combined</th>
                          </tr>
                        </thead>

                        <tbody className="text-gray-700">
                          <tr>
                            <td className="border px-4 py-2">TPH (Tons per hour)</td>
                            <td className="border px-4 py-2 text-center">{content?.kpi_tph_p1 || "400 tons"}</td>
                            <td className="border px-4 py-2 text-center">{content?.kpi_tph_p2 || "400 tons"}</td>
                            <td className="border px-4 py-2 text-center font-semibold">{content?.kpi_tph || "800 tons"}</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Daily Production</td>
                            <td className="border px-4 py-2 text-center">{content?.kpi_daily_p1 || "4,464 tons"}</td>
                            <td className="border px-4 py-2 text-center">{content?.kpi_daily_p2 || "4,464 tons"}</td>
                            <td className="border px-4 py-2 text-center font-semibold">{content?.kpi_daily || "8,928 tons"}</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">Monthly Production</td>
                            <td className="border px-4 py-2 text-center">{content?.kpi_monthly_p1 || "120,000 tons"}</td>
                            <td className="border px-4 py-2 text-center">{content?.kpi_monthly_p2 || "120,000 tons"}</td>
                            <td className="border px-4 py-2 text-center font-semibold">{content?.kpi_monthly || "240,000 tons"}</td>
                          </tr>

                          <tr className="bg-gray-50">
                            <td colSpan={4} className="border px-4 py-2 font-semibold text-gray-900">
                              Aggregate Production Percentage wise
                            </td>
                          </tr>

                          <tr>
                            <td className="border px-4 py-2">20 mm</td>
                            <td className="border px-4 py-2 text-center">{content?.agg_20_p1 || "44%"}</td>
                            <td className="border px-4 py-2 text-center">{content?.agg_20_p2 || "41%"}</td>
                            <td className="border px-4 py-2 text-center font-semibold">{content?.agg_20_comb || "42%"}</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">10 mm</td>
                            <td className="border px-4 py-2 text-center">{content?.agg_10_p1 || "28%"}</td>
                            <td className="border px-4 py-2 text-center">{content?.agg_10_p2 || "23%"}</td>
                            <td className="border px-4 py-2 text-center font-semibold">{content?.agg_10_comb || "25%"}</td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2">0–5 mm</td>
                            <td className="border px-4 py-2 text-center">{content?.agg_05_p1 || "28%"}</td>
                            <td className="border px-4 py-2 text-center">{content?.agg_05_p2 || "36%"}</td>
                            <td className="border px-4 py-2 text-center font-semibold">{content?.agg_05_comb || "33%"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Notes */}
                  <Reveal variant="fadeUp" delayMs={150}>
                    <div className="mt-4 rounded-2xl border bg-gray-50 p-4">
                      <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">Blasting Notes</div>
                      <div className="mt-2 space-y-1 text-xs text-gray-600 leading-relaxed whitespace-pre-line text-justify">
                        {content?.blasting_notes || `• Blasting carried out 3–4 times/month with spacing & burden 3.5m × 3.5m (avg 10m).\n• Each blasting ≈ 20,000 m³ (≈ 56,000 metric tons).\nTotal per month = 4 × 56,000 tons = 224,000 tons`}
                      </div>
                    </div>
                  </Reveal>
                </Reveal>

                {/* Image + caption */}
                <Reveal variant="fadeRight" className="lg:col-span-5">
                  <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
                    <img src={content?.plant_view_image || "/gabbro/production-plant.jpg"} alt="Crusher Plant" className="h-[320px] w-full object-cover" />
                    <div className="p-5">
                      <div className="text-xs uppercase tracking-widest text-gray-500">Plant View</div>
                      <div className="mt-1 text-sm font-semibold text-gray-900">{content?.plant_view_title || "Crusher & Screening Operations"}</div>
                      <p className="mt-2 text-sm text-gray-700 leading-relaxed text-justify">
                        {content?.plant_view_desc || "Production capacity is supported by multi-stage crushing and consistent grading to meet infrastructure and concrete project requirements."}
                      </p>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default AboutGabbroAlt;
